import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    StickyNote,
    Receipt,
    Package,
    Wallet,
    ClipboardCheck,
    AlertTriangle,
    BarChart3,
    Building2,
    KeyRound,
    UserPlus,
    Map as MapIcon,
    Sparkles,
    Users,
    Search,
    LayoutDashboard,
    Calendar,
    FileText,
    Tag,
    X,
    Check,
    ArrowRight,
} from "lucide-react";

/* =====================================================================
 * Module groups – matches pricing add-on names 1:1
 * ===================================================================== */
const groups = [
    {
        slug: "core",
        title: "Cleany Core",
        priceLabel: "Inkluderet i grundpakken",
        coreLabel: true,
        tagline: "Fundamentet — det I altid har med jer.",
        description:
            "Cleany Core er det fælles fundament i platformen. Alle moduler bygger ovenpå Core. Indeholder 1 administrator og op til 20 medarbejdere med app-adgang, samt alle de funktioner der gør hverdagen overskuelig fra dag ét.",
        screen: "/screens/dashboard.jpg",
        span: "md:col-span-3",
        accent: true,
        subModules: [
            {
                slug: "core-virksomheder",
                icon: Building2,
                title: "Kunder & kontaktpersoner",
                tagline: "Hvert kundeforhold samlet ét sted.",
                description:
                    "Opret kunder, leverandører og kontaktpersoner. Knyt opgaver, fakturering og noter direkte til virksomheden.",
                bullets: [
                    "Centralt kunde- og leverandørkartotek",
                    "Kontaktpersoner pr. virksomhed",
                    "Filtrér på type og ansvarlig",
                ],
                screen: "/screens/virksomheder.jpg",
            },
            {
                slug: "core-personer",
                icon: Users,
                title: "Brugere & rettigheder",
                tagline: "Styr på alle der bruger Cleany.",
                description:
                    "Administratorer, servicechefer, medarbejdere med app-adgang og kunde-backend-adgang — med detaljeret rettighedsstyring.",
                bullets: [
                    "5 brugertyper med rette niveauer",
                    "Detaljeret rettighedsstyring",
                    "Hurtig søgning på tværs af alle personer",
                ],
                screen: "/screens/personer.jpg",
            },
            {
                slug: "core-noter",
                icon: StickyNote,
                title: "Noter & Journal",
                tagline: "Vigtig viden går aldrig tabt.",
                description:
                    "Generelle noter, opfølgninger, lønnoter og personalenoter — koblet til kunder, opgaver eller medarbejdere.",
                bullets: [
                    "Note-typer: kunde, drift, leverandør, personale",
                    "Opfølgningsdatoer med påmindelser",
                    "Privat- og delte noter",
                ],
                screen: "/screens/journal.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Noter og journal giver virksomheden mulighed for at registrere vigtig information, løbende hændelser og historik omkring kunder, lokationer, opgaver eller samarbejder. Funktionen samler relevante bemærkninger og hændelser ét sted, så informationen er nem at finde og følge over tid.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er der ofte mange små, men vigtige detaljer, der skal huskes i den daglige drift. Med noter og journal kan medarbejdere og ledelse registrere observationer, aftaler, ændringer eller særlige forhold, så vigtig viden ikke går tabt. Det skaber bedre kommunikation internt og gør det lettere at sikre ensartet service.\n\nSamtidig giver funktionen en historik, som kan bruges til opfølgning, dokumentation og dialog med både kunder og medarbejdere. Det bidrager til en mere struktureret og professionel drift.",
                    },
                ],
            },
            {
                slug: "core-kalender",
                icon: Calendar,
                title: "Kalender & opgaver",
                tagline: "Dagens, ugens og månedens drift.",
                description:
                    "Saml alle abonnementer og ad hoc-opgaver i én kalender. Tildel medarbejdere og se status live.",
                bullets: [
                    "Personlige og fælles kalendere",
                    "Tildeling af opgaver til medarbejdere",
                    "Live-status: planlagt, i gang, færdig",
                ],
                screen: "/screens/dashboard.jpg",
            },
            {
                slug: "core-dashboard",
                icon: LayoutDashboard,
                title: "Basic dashboard",
                tagline: "Driftens puls — på et øjekast.",
                description:
                    "Overblik over dagens opgaver, medarbejdere på job, vare-bestillinger og ikke-udførte opgaver — opdateret live.",
                bullets: [
                    "Live drift-overblik for i dag",
                    "Hurtig adgang til vigtige genveje",
                    "Tilpasset hver brugertype",
                ],
                screen: "/screens/dashboard.jpg",
            },
            {
                slug: "core-dokumenter",
                icon: FileText,
                title: "Dokumenter & filer",
                tagline: "Filer hvor de hører hjemme.",
                description:
                    "Vedhæft kontrakter, planer, billeder og bilag direkte på kunder, opgaver og medarbejdere.",
                bullets: [
                    "Dokumenter pr. kunde, opgave eller medarbejder",
                    "PDF, billeder og Office-filer",
                    "Foto-upload fra mobilen",
                ],
                screen: "/screens/dokumenter.svg",
            },
            {
                slug: "core-sogning",
                icon: Search,
                title: "Søgning på tværs",
                tagline: "Find alt — på under 2 sekunder.",
                description:
                    "Smart finder på tværs af kunder, personer, opgaver, noter og dokumenter. Cmd/Ctrl + K åbner søgefeltet hvor som helst.",
                bullets: [
                    "Søger på tværs af alle moduler",
                    "Hurtige genveje og filtre",
                    "Tastatur-genvej overalt i app'en",
                ],
                screen: "/screens/smartfinder.svg",
            },
        ],
    },
    {
        slug: "drift-kvalitet",
        title: "Drift & Kvalitet",
        priceLabel: "Tillægsmodul",
        tagline: "Kørsel, besøg, kvalitet og reklamationer.",
        description:
            "Den operationelle motor i Cleany. Registrér kørsel, gennemfør kvalitetskontroller, planlæg kundebesøg og luk reklamationer — alt med fuld sporbarhed.",
        screen: "/screens/map.jpg",
        span: "md:col-span-2",
        accent: true,
        subModules: [
            {
                slug: "koreopgaver",
                icon: MapIcon,
                title: "Kørsel",
                tagline: "Visualiser alle dagens opgaver på et live-kort.",
                description:
                    "Visualiser alle dagens opgaver på et live-kort.",
                bullets: [
                    "Live-kort med klyngede opgavemarkører",
                    "GPS-tracking og rejsetid for hver medarbejder",
                    "Filtrér på status: skal påbegyndes, i gang, planlagt",
                ],
                screen: "/screens/map.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Køreopgaver i Cleany gør det muligt at registrere køretid og antal kilometer pr. kunde eller lokation. Funktionen er udviklet til at dokumentere den kørsel, der er forbundet med arbejdet hos kunden, så virksomheden får et præcist grundlag for både fakturering og aflønning.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er kørsel ofte en vigtig del af den samlede leverance. Med køreopgaver kan medarbejdernes køretid og antal kørte kilometer registreres korrekt på den enkelte kunde eller adresse. Det sikrer, at både tidsforbrug og transportomkostninger bliver håndteret præcist og gennemsigtigt.\n\nFunktionen gør det lettere at fakturere kunder korrekt for kørsel, samtidig med at virksomheden kan aflønne medarbejdere retvisende for den tid og afstand, der bruges på opgaven. Det giver en mere professionel og effektiv drift samt et bedre økonomisk overblik.",
                    },
                    {
                        heading: "5 grunde til at benytte Kørsel",
                        items: [
                            "Sikrer korrekt registrering af køretid og kilometer",
                            "Understøtter præcis fakturering af kørsel til kunden",
                            "Giver et retvisende grundlag for medarbejdernes aflønning",
                            "Skaber bedre dokumentation af transportforbrug pr. kunde eller lokation",
                            "Bidrager til en mere gennemsigtig og effektiv drift",
                        ],
                        numbered: true,
                    },
                ],
            },
            {
                slug: "besog",
                icon: ClipboardCheck,
                title: "Besøg & Kvalitet",
                tagline: "Dokumenteret kvalitet hos hver kunde.",
                description:
                    "Planlæg kvalitets- og kundebesøg med tjeklister, billeddokumentation og automatiske rapporter. Alt arkiveres pr. kunde og kan deles direkte med dem.",
                bullets: [
                    "Skabeloner for kvalitetstjek og kundebesøg",
                    "Foto-dokumentation indlejret i besøget",
                    "Automatiske rapporter pr. kunde",
                    "Reklamationsbesøg og opfølgningsbesøg samlet",
                ],
                screen: "/screens/besog.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Besøg og kvalitet giver rengøringsvirksomheden et samlet værktøj til at planlægge kundebesøg, gennemføre kvalitetskontroller og dokumentere opfølgning på lokationer. Funktionen bruges til at registrere observationer, vurdere rengøringskvaliteten og sikre, at dialog, aftaler og eventuelle forbedringer bliver dokumenteret på en struktureret måde.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "Med Besøg og kvalitet får virksomheden et effektivt overblik over både planlagte kundebesøg og løbende kvalitetssikring. Det gør det lettere at følge op på kunder, opdage fejl eller mangler i tide og sikre, at servicen altid lever op til virksomhedens standarder. Samtidig samles informationer ét sted, så både ledelse og drift hurtigt kan få adgang til den nødvendige historik.\n\nFunktionen understøtter en mere professionel og proaktiv tilgang til kundepleje. I stedet for først at reagere, når en kunde melder et problem, kan virksomheden arbejde forebyggende og kontinuerligt med at forbedre kvaliteten.",
                    },
                    {
                        heading: "5 grunde til at benytte Besøg & Kvalitet",
                        items: [
                            "Giver ét samlet overblik over kundebesøg og kvalitetssikring",
                            "Gør opfølgning lettere med tydelig dokumentation og historik",
                            "Hjælper med at opdage udfordringer tidligt og handle hurtigt",
                            "Styrker samarbejdet mellem drift, ledelse og kundeservice",
                            "Bidrager til en mere stabil og høj rengøringskvalitet",
                        ],
                        numbered: true,
                    },
                ],
            },
            {
                slug: "reklamationer",
                icon: AlertTriangle,
                title: "Reklamationer",
                tagline: "Luk reklamationer hurtigt, lær af dem altid.",
                description:
                    "Modtag, kategoriser og luk kundereklamationer med fuld sporbarhed — fra første henvendelse til endelig opfølgning. Med fuld statistik.",
                bullets: [
                    "Centraliseret reklamations-inbox",
                    "Auto-tildeling til ansvarlig medarbejder",
                    "Sporbarhed fra modtagelse til lukning",
                    "Reklamations-statistik",
                ],
                screen: "/screens/reklamationer.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Reklamationer giver rengøringsvirksomheden et samlet sted til at registrere, følge op på og håndtere kunders reklamationer. Funktionen gør det muligt at dokumentere, hvad kunden har klaget over, hvilken opgave det vedrører, og hvordan sagen er blevet behandlet.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "Når en kunde oplever en fejl eller mangel, er det vigtigt at kunne reagere hurtigt og struktureret. Med reklamationer kan virksomheden samle al relevant information ét sted, så sagen bliver nemmere at følge fra første henvendelse til endelig løsning.\n\nSamtidig giver funktionen bedre dokumentation, hvilket er en stor fordel i dialogen med kunden. Virksomheden kan se historik, beskrivelser og eventuelle tilknyttede opgaver, så behandlingen af reklamationen bliver mere professionel og gennemsigtig.",
                    },
                    {
                        heading: "5 grunde til at benytte Reklamationer",
                        items: [
                            "Giver et klart overblik over alle kundeklager og deres status",
                            "Sikrer hurtig og struktureret opfølgning på fejl og mangler",
                            "Skaber bedre dokumentation i dialogen med kunden",
                            "Understøtter læring og forbedring af den daglige drift",
                            "Bidrager til højere kundetilfredshed gennem professionel håndtering",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
    {
        slug: "analyse",
        title: "Analyse & Rapportering",
        priceLabel: "Tillægsmodul",
        tagline: "Statistikker, KPI-dashboards og rapporter.",
        description:
            "Få indsigt i drift, kvalitet, økonomi og aktivitet. Træf beslutninger på baggrund af data — ikke mavefornemmelse.",
        screen: "/screens/statistikker.jpg",
        subModules: [
            {
                slug: "statistikker",
                icon: BarChart3,
                title: "Statistikker",
                tagline: "Beslutninger på baggrund af data.",
                description:
                    "Realtids-dashboards over driftens KPI'er: dækningsgrad, kvalitet, reklamationer, omsætning pr. medarbejder. Eksportér til Excel eller PDF.",
                bullets: [
                    "KPI-dashboards i realtid",
                    "Dækningsgrad, kvalitet, reklamations-rate",
                    "Eksport til Excel og PDF",
                    "Tilpassede rapporter pr. ledelseslag",
                ],
                screen: "/screens/statistikker.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Statistikker i Cleany giver virksomheden et samlet overblik over udviklingen i drift, kvalitet, økonomi og aktivitet. Funktionen samler relevante nøgletal og gør det muligt at følge med i, hvordan virksomheden præsterer over tid.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er det en stor fordel at kunne se data samlet ét sted. Med statistikker bliver det lettere at identificere tendenser, følge op på mål og træffe beslutninger på et bedre grundlag.\n\nStatistikker hjælper ledelsen med at få indblik i, hvor der er behov for forbedringer, og hvor virksomheden klarer sig godt. Det gør det muligt at arbejde mere datadrevet og effektivt i den daglige drift.",
                    },
                    {
                        heading: "5 grunde til at benytte Statistikker",
                        items: [
                            "Giver et tydeligt overblik over virksomhedens udvikling",
                            "Understøtter bedre beslutninger på baggrund af data",
                            "Hjælper med at identificere mønstre og tendenser",
                            "Gør det lettere at følge mål og resultater",
                            "Skaber et stærkere grundlag for optimering af driften",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
    {
        slug: "lager",
        title: "Lager & Vareflow",
        priceLabel: "Tillægsmodul",
        tagline: "Varekatalog, lager, bestillinger og anlægsarkiv.",
        description:
            "Centralt overblik over rengøringsmidler, lagerværdi og forbrug pr. kunde og opgave — samt anlægsarkiv for maskiner og udstyr.",
        screen: "/screens/varekatalog.jpg",
        span: "md:col-span-2",
        subModules: [
            {
                slug: "varekatalog",
                icon: Package,
                title: "Varekatalog & Lager",
                tagline: "Centralt overblik over rengøringsmidler.",
                description:
                    "Pakker, grupper og enkeltvarer — med samlet lagerværdi, indkøbspris og bestillinger. Forbrug kobles direkte til opgaver og kunder.",
                bullets: [
                    "Pakker, grupper og enkeltvarer",
                    "Lagerværdi i realtid",
                    "Bestillinger og varemodtagelse",
                    "Forbrug pr. kunde og opgave",
                ],
                screen: "/screens/varekatalog.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Varekatalog og lager giver rengøringsvirksomheden et samlet overblik over varer, produkter og lagerbeholdning. Funktionen bruges til at registrere, hvilke varer virksomheden har til rådighed, hvor mange der er på lager, og hvilke produkter der bruges i den daglige drift.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er det vigtigt altid at have de nødvendige produkter og materialer klar til opgaverne. Med varekatalog og lager kan virksomheden nemt følge med i forbruget, undgå at løbe tør for vigtige varer og planlægge genbestillinger i god tid.\n\nSamtidig giver funktionen et bedre økonomisk overblik, fordi virksomheden kan følge, hvilke varer der bruges hvor, og hvor der eventuelt er mulighed for at optimere indkøb og forbrug.",
                    },
                    {
                        heading: "5 grunde til at benytte Varekatalog & Lager",
                        items: [
                            "Giver fuldt overblik over varer og lagerbeholdning",
                            "Hjælper med at undgå mangler i det daglige arbejde",
                            "Gør genbestilling lettere og mere rettidig",
                            "Skaber bedre kontrol med forbrug og indkøb",
                            "Understøtter en mere effektiv og økonomisk drift",
                        ],
                        numbered: true,
                    },
                ],
            },
            {
                slug: "anlaegsarkiv",
                icon: Building2,
                title: "Anlægsarkiv",
                tagline: "Hver maskine som sit eget kort.",
                description:
                    "Hold styr på maskiner, udstyr og inventar — koblet til den kunde de er udleveret til, med strukturerede felter for model, serienummer, garanti og servicehistorik.",
                bullets: [
                    "Strukturerede asset-records pr. maskine",
                    "Serienumre, modeller og garantioplysninger",
                    "Koblet til kunden maskinen er udleveret til",
                    "Servicehistorik og vedligeholdelsesdatoer",
                ],
                screen: "/screens/anlaegsarkiv.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Anlægsarkiv i Cleany giver virksomheden et samlet sted til at registrere og holde styr på anlægsaktiver, som for eksempel maskiner, udstyr eller andet inventar. Funktionen gør det muligt at bevare overblikket over virksomhedens værdier og sikre, at vigtig information om udstyret er samlet ét sted.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er det vigtigt at have styr på det udstyr og de aktiver, der bruges i den daglige drift. Med anlægsarkiv kan virksomheden nemt følge med i, hvad der er registreret, hvor det hører til, og hvordan det eventuelt skal håndteres over tid.\n\nFunktionen kan også understøtte planlægning, dokumentation og intern kontrol, så virksomheden står stærkere i forhold til både drift og økonomi.",
                    },
                    {
                        heading: "5 grunde til at benytte Anlægsarkiv",
                        items: [
                            "Giver overblik over virksomhedens anlægsaktiver",
                            "Samler vigtig dokumentation ét sted",
                            "Understøtter bedre styring af udstyr og inventar",
                            "Hjælper med intern kontrol og opfølgning",
                            "Bidrager til en mere professionel administration",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
    {
        slug: "adgang-nogle",
        title: "Adgang & Nøgle håndtering",
        priceLabel: "Tillægsmodul",
        tagline: "Nøgledatabase med fuld sporbarhed.",
        description:
            "Hold styr på kundenøgler, adgangsbrikker og koder — koblet til kunder og medarbejdere med fuld udleverings- og returneringslog.",
        screen: "/screens/anlaegsarkiv.jpg",
        subModules: [
            {
                slug: "nogledatabase",
                icon: KeyRound,
                title: "Nøgledatabase",
                tagline: "Styr på hver eneste kundenøgle.",
                description:
                    "Hvem har hvilken nøgle til hvilken kunde — og hvornår blev den udleveret eller returneret? Slut med Excel-ark og glemte nøgler.",
                bullets: [
                    "Nøgler kobles til kunde og medarbejder",
                    "Udleverings- og returneringslog",
                    "App-adgang til adgangskoder, kodeord m.v.",
                    "Nøglen er væk! Bliv notificeret hvis nøglen findes med GPS-lokation",
                ],
                screen: "/screens/anlaegsarkiv.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Nøgledatabasen bruges til at registrere og holde styr på virksomhedens nøgler, adgangsbrikker, koder og andre adgangsoplysninger knyttet til kunder og adresser. Den giver et samlet overblik over, hvem der har hvilken nøgle, hvor den hører til, og om den er aktiv, udleveret eller returneret.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er adgang til kundernes lokationer helt afgørende. Nøgledatabasen gør det lettere at undgå fejl, misforståelser og tabte nøgler. Medarbejdere og ledelse kan hurtigt se, hvilke nøgler der findes, hvem der har dem, og om der mangler noget ved aflevering eller skift af personale.",
                    },
                    {
                        heading: "5 grunde til at benytte Nøgledatabasen",
                        items: [
                            "Bedre overblik over alle nøgler og adgangsoplysninger",
                            "Mindre risiko for fejl og bortkomne nøgler",
                            "Nem overdragelse ved medarbejderskift eller ferie",
                            "Højere sikkerhed for både virksomhed og kunde",
                            "Mere effektiv drift fordi oplysningerne er samlet ét sted",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
    {
        slug: "okonomi",
        title: "Økonomi & Afregning",
        priceLabel: "Tillægsmodul",
        tagline: "Fakturering, self-billing og e-conomic / Dinero.",
        description:
            "Fakturér direkte fra opgaver. Send med ét klik til e-conomic eller Dinero. Auto-afregn underleverandører.",
        screen: "/screens/fakturering.jpg",
        span: "md:col-span-2",
        subModules: [
            {
                slug: "fakturering",
                icon: Receipt,
                title: "Fakturering",
                tagline: "Fakturér direkte fra opgaver.",
                description:
                    "Saml alle abonnementer, ad hoc-opgaver og varesalg i én faktura-buffer. Send med ét klik til e-conomic, Dinero.",
                bullets: [
                    "Faktura-buffer på tværs af opgavetyper",
                    "Direkte overførsel til e-conomic/Dinero",
                    "Markering af opgaver uden finansaftale, så ingen opgaver bliver glemt",
                    "Periodisk og ad hoc fakturering",
                ],
                screen: "/screens/fakturering.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Fakturering i Cleany samler virksomhedens grundlag for at oprette og sende fakturaer til kunderne. Funktionen hjælper med at holde styr på aftaler, ydelser, timer, kørsel og andre fakturerbare elementer, så faktureringen bliver korrekt og ensartet.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er det afgørende, at alt udført arbejde bliver faktureret korrekt og til tiden. Med faktureringsfunktionen får virksomheden et bedre overblik over, hvad der skal faktureres, og hvilke ydelser der hører til hvilken kunde eller lokation.\n\nSamtidig gør funktionen det lettere at sikre sammenhæng mellem det arbejde, der udføres, og det beløb, der sendes til kunden.",
                    },
                    {
                        heading: "5 grunde til at benytte Fakturering",
                        items: [
                            "Sikrer korrekt fakturering af udført arbejde og aftalte ydelser",
                            "Reducerer fejl og glemsler i den økonomiske opfølgning",
                            "Sparer tid ved at samle faktureringsgrundlaget ét sted",
                            "Giver bedre overblik over kundernes betalinger og ydelser",
                            "Understøtter en mere professionel og effektiv økonomistyring",
                        ],
                        numbered: true,
                    },
                ],
            },
            {
                slug: "self-billing",
                icon: Wallet,
                title: "Self-billing",
                tagline: "Auto-afregning til underleverandører.",
                description:
                    "Underleverandører bliver automatisk afregnet baseret på udførte opgaver. Ingen manuelle bilag, ingen tvivl om hvad der er aftalt.",
                bullets: [
                    "Automatisk afregning ved opgaveafslutning",
                    "Aftalte enhedspriser pr. underleverandør",
                    "Måneds-/ugevis afregningskørsler",
                    "Fuld bilagslog til revisor",
                ],
                screen: "/screens/fakturering.jpg",
                sections: [
                    {
                        heading: "Funktion",
                        body: "Selfbilling i Cleany gør det muligt at håndtere selvfakturering på en enkel og struktureret måde. Funktionen understøtter processer, hvor en kunde eller samarbejdspartner udsteder fakturagrundlaget på vegne af virksomheden, så registrering, afstemning og økonomisk opfølgning bliver samlet ét sted.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For rengøringsvirksomheder, der arbejder med aftaler eller samarbejder, hvor selvfakturering indgår, er det vigtigt at have et klart og gennemsigtigt overblik over, hvad der skal bogføres og afstemmes. Med Selfbilling bliver det lettere at holde styr på fakturagrundlaget, minimere manuelt arbejde og sikre, at økonomien er korrekt registreret.\n\nFunktionen bidrager til en mere effektiv administration, fordi virksomheden kan arbejde mere systematisk med kontrol, opfølgning og dokumentation.",
                    },
                    {
                        heading: "5 grunde til at benytte Self-billing",
                        items: [
                            "Giver struktur i håndteringen af selvfakturerede bilag",
                            "Reducerer manuelt arbejde i den økonomiske administration",
                            "Skaber bedre overblik over fakturagrundlag og afstemning",
                            "Mindsker risikoen for fejl i bogføring og opfølgning",
                            "Understøtter en mere effektiv og professionel økonomiproces",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
    {
        slug: "salg",
        title: "Salg & Leads",
        priceLabel: "Tillægsmodul",
        tagline: "Lead-pipeline, opfølgning og tilbud.",
        description:
            "Saml alle nye henvendelser og igangværende salgsmuligheder ét sted. Konvertér interesserede til faste kunder.",
        screen: null,
        comingSoon: true,
        subModules: [
            {
                slug: "leads",
                icon: UserPlus,
                title: "Leads & Salg",
                tagline: "Konvertér interesserede til faste kunder.",
                description:
                    "Indsaml leads, planlæg opfølgninger og send tilbud direkte fra platformen. Når et lead bliver til en kunde, oprettes virksomhed og kontaktperson automatisk.",
                bullets: [
                    "Lead-pipeline med stadier",
                    "Opfølgnings-påmindelser",
                    "Tilbudsudsendelse fra platformen",
                    "Auto-konvertering til kunde og kontaktperson",
                ],
                screen: null,
                comingSoon: true,
                sections: [
                    {
                        heading: "Funktion",
                        body: "Leads og salg giver virksomheden mulighed for at holde styr på nye potentielle kunder, salgsaktiviteter og igangværende muligheder. Funktionen samler information om leads, kontaktforløb og salgsstatus, så virksomheden kan arbejde struktureret med at omsætte interesserede emner til konkrete aftaler.",
                    },
                    {
                        heading: "Hvordan den hjælper i dagligdagen",
                        body: "For en rengøringsvirksomhed er det vigtigt at have styr på både nye henvendelser og den løbende salgsproces. Med Leads og salg bliver det lettere at følge op på potentielle kunder, sikre at ingen henvendelser bliver glemt, og skabe et bedre overblik over, hvor i processen de enkelte muligheder befinder sig.\n\nFunktionen hjælper virksomheden med at arbejde mere målrettet og professionelt med salg.",
                    },
                    {
                        heading: "5 grunde til at benytte Leads & Salg",
                        items: [
                            "Giver overblik over nye og igangværende salgsmuligheder",
                            "Sikrer bedre opfølgning på potentielle kunder",
                            "Reducerer risikoen for tabte leads",
                            "Understøtter en mere struktureret salgsproces",
                            "Hjælper virksomheden med at omsætte muligheder til vækst",
                        ],
                        numbered: true,
                    },
                ],
            },
        ],
    },
];

/* =====================================================================
 * Render
 * ===================================================================== */
export default function Modules() {
    const [active, setActive] = useState(null);

    return (
        <section id="moduler" className="py-24 md:py-32 bg-white" data-testid="modules-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Moduler
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                        Alt hvad du skal bruge til{" "}
                        <span className="text-gradient-blue">driften</span>
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                        Klik på et modul for at se det i aktion. Cleany Core er fundamentet
                        — derudover vælger du de tillægsmoduler du har brug for.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="modules-grid">
                    {groups.map((g, i) => (
                        <ModuleTile key={g.slug} g={g} i={i} onOpen={() => setActive(g)} />
                    ))}
                </div>
            </div>

            <ModuleDialog mod={active} onClose={() => setActive(null)} />
        </section>
    );
}

function ModuleTile({ g, i, onOpen }) {
    return (
        <motion.button
            type="button"
            onClick={onOpen}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            className={`group relative rounded-2xl border border-slate-200 hover:border-sky-300 bg-white text-left overflow-hidden ${g.span || ""} min-h-[280px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 shadow-sm hover:shadow-xl hover:shadow-sky-900/10 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-500 ease-out`}
            data-testid={`module-card-${g.slug}`}
        >
            {g.screen && (
                <>
                    {/* Background screenshot — scales + de-saturates on hover */}
                    <div
                        className="absolute inset-0 bg-cover bg-top opacity-25 grayscale scale-100 group-hover:opacity-95 group-hover:grayscale-0 group-hover:scale-[1.04] transition-[opacity,filter,transform] duration-[700ms] ease-out"
                        style={{ backgroundImage: `url(${g.screen})` }}
                        aria-hidden="true"
                    />
                    {/* White readability gradient — collapses on hover so image shines */}
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/90 to-white/55 group-hover:from-white/95 group-hover:via-white/55 group-hover:to-white/5 transition-all duration-[700ms] ease-out" />
                    {/* Sky-blue glow on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[700ms] bg-gradient-to-br from-sky-400/0 via-sky-400/0 to-sky-500/15 pointer-events-none" aria-hidden="true" />
                    {/* "Demo data" stamp — only visible on hover (when screenshot is exposed) */}
                    <span
                        className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full
                                   bg-white/85 backdrop-blur border border-slate-200
                                   px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider
                                   text-slate-600 shadow-sm
                                   opacity-0 group-hover:opacity-100 transition-opacity duration-[700ms]
                                   pointer-events-none z-[1]"
                        aria-hidden="true"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Demo data
                    </span>
                </>
            )}
            {!g.screen && g.accent && (
                <div className="absolute inset-0 bg-gradient-to-br from-sky-50/60 to-white" />
            )}

            <div className="relative p-6 flex flex-col h-full">
                {g.comingSoon && (
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                        KOMMER SNART
                    </span>
                )}
                <div className="flex items-center gap-2">
                    <div className="h-11 w-11 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0">
                        {g.coreLabel ? <Sparkles className="h-5 w-5" /> : <Tag className="h-5 w-5" />}
                    </div>
                    <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                            g.coreLabel
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-sky-50 text-sky-700 border border-sky-200"
                        }`}
                    >
                        {g.coreLabel ? "✓ Inkluderet" : g.priceLabel}
                    </span>
                </div>
                <h3 className="mt-4 font-display text-xl md:text-2xl font-semibold text-slate-900">
                    {g.title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {g.tagline}
                </p>
                <ul className="mt-3 flex flex-wrap gap-1.5">
                    {g.subModules.map((s) => (
                        <li
                            key={s.slug}
                            className="inline-flex text-[11px] font-semibold rounded-full bg-white/80 backdrop-blur border border-slate-200 group-hover:bg-white/65 group-hover:border-sky-200 px-2 py-0.5 text-slate-700 transition-colors duration-500"
                        >
                            {s.title}
                        </li>
                    ))}
                </ul>
                <div className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 group-hover:text-white transition-colors duration-300">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-transparent group-hover:bg-sky-600 group-hover:shadow-md group-hover:shadow-sky-600/30 transition-all duration-300">
                        Læs mere
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                </div>
            </div>
        </motion.button>
    );
}

function ModuleDialog({ mod, onClose }) {
    return (
        <AnimatePresence>
            {mod && (
                <motion.div
                    className="fixed inset-0 z-[110] flex items-start justify-center p-4 overflow-y-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="module-dialog"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
                    <motion.div
                        initial={{ scale: 0.96, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.96, opacity: 0, y: 20 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-5xl my-8 rounded-3xl bg-white shadow-2xl border border-slate-200"
                    >
                        <button
                            onClick={onClose}
                            className="sticky top-4 ml-auto mr-4 z-10 h-10 w-10 rounded-full bg-white/90 hover:bg-white border border-slate-200 text-slate-600 hover:text-slate-900 inline-flex items-center justify-center shadow-sm float-right"
                            data-testid="module-dialog-close"
                            aria-label="Luk"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {/* Header */}
                        <div className="px-8 md:px-10 pt-10 pb-6">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                                        mod.coreLabel
                                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                            : "bg-sky-50 text-sky-700 border border-sky-200"
                                    }`}
                                >
                                    {mod.coreLabel ? "✓ Inkluderet i grundpakken" : mod.priceLabel}
                                </span>
                                {mod.comingSoon && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                                        KOMMER SNART
                                    </span>
                                )}
                            </div>
                            <h3 className="mt-4 font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                                {mod.title}
                            </h3>
                            <p className="mt-3 text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
                                {mod.description}
                            </p>
                            <div className="mt-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Indeholder ({mod.subModules.length} {mod.subModules.length === 1 ? "modul" : "moduler"})
                            </div>
                        </div>

                        {/* Sub-modules */}
                        <div className="border-t border-slate-200 bg-gradient-to-b from-slate-50/40 to-white px-6 md:px-10 py-8 space-y-10">
                            {mod.subModules.map((s) => (
                                <SubModule key={s.slug} s={s} />
                            ))}
                        </div>

                        <div className="px-8 md:px-10 pb-10 pt-2 text-center">
                            <button
                                onClick={onClose}
                                className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium px-6 h-11"
                            >
                                Luk
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function SubModule({ s }) {
    return (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={`submodule-${s.slug}`}>
            <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-6 md:p-8">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600">
                            <s.icon className="h-5 w-5" strokeWidth={1.75} />
                        </div>
                        <h4 className="font-display text-xl md:text-2xl font-bold text-slate-900">{s.title}</h4>
                        {s.comingSoon && (
                            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                KOMMER SNART
                            </span>
                        )}
                    </div>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{s.description}</p>
                    <ul className="mt-4 space-y-2">
                        {s.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                                <Check className="h-4 w-4 mt-0.5 text-sky-600 shrink-0" />
                                {b}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="bg-gradient-to-br from-sky-50/40 to-slate-50 p-5 md:p-6 flex items-center">
                    {s.screen ? (
                        <div className="rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden w-full">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-200 bg-slate-50">
                                <div className="h-2 w-2 rounded-full bg-red-300" />
                                <div className="h-2 w-2 rounded-full bg-yellow-300" />
                                <div className="h-2 w-2 rounded-full bg-emerald-300" />
                                <div className="ml-2 text-[10px] text-slate-400 font-mono truncate">my.cleany.dk / {s.slug}</div>
                                <span
                                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                                    title="Skærmbillederne viser anonymiseret demo-data — ikke rigtige kunder eller medarbejdere."
                                    data-testid="demo-data-stamp"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    Demo data
                                </span>
                            </div>
                            <img src={s.screen} alt={s.title} className="w-full block" />
                        </div>
                    ) : (
                        <div className="w-full aspect-[4/3] rounded-xl border border-dashed border-slate-300 bg-white/60 flex items-center justify-center text-center p-6">
                            <div>
                                <div className="font-display text-base font-semibold text-slate-700">På vej</div>
                                <p className="mt-1 text-xs text-slate-500">Tilgængelig efter launch.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {s.sections && s.sections.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50/40 px-6 md:px-8 py-6 space-y-6">
                    {s.sections.map((sec, k) => (
                        <div key={k}>
                            <h5 className="font-display text-base md:text-lg font-bold text-slate-900">
                                {sec.heading}
                            </h5>
                            {sec.body && (
                                <div className="mt-2 space-y-2.5">
                                    {sec.body.split(/\n\n+/).map((p, j) => (
                                        <p key={j} className="text-[14px] text-slate-700 leading-relaxed">{p}</p>
                                    ))}
                                </div>
                            )}
                            {sec.items && (
                                <ul className="mt-3 space-y-2">
                                    {sec.items.map((it, j) => (
                                        <li key={j} className="flex items-start gap-2.5 text-[14px] text-slate-700">
                                            {sec.numbered ? (
                                                <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-sky-600 text-white text-[10px] font-bold inline-flex items-center justify-center tabular-nums">
                                                    {j + 1}
                                                </span>
                                            ) : (
                                                <Check className="h-4 w-4 mt-1 text-sky-600 shrink-0" />
                                            )}
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
